/**
 * Client-side mirror of src/api/accounting/ap-invoice-document-groups.ts for badges and split modals.
 */

export interface ApInvoiceDocumentGroupInput {
  id: string;
  filename: string;
  kind: string;
}

export interface ApInvoiceSuggestedDocumentGroup {
  invoice_key: string | null;
  suggested_mfr_invoice_number: string | null;
  document_ids: string[];
  filenames: string[];
}

export interface ApInvoiceDocumentGroupSuggestion {
  groups: ApInvoiceSuggestedDocumentGroup[];
  unassigned_document_ids: string[];
  duplicate_document_ids: string[];
  suggested_invoice_count: number;
  warnings: string[];
}

const IV_PATTERN = /\bIV\s*0*(\d{4,})(?!\d)/i;
const PL_PATTERN = /\bPL\s*#\s*0*(\d{4,})(?!\d)/i;

export function parseSuntronicInvoiceKeyFromFilename(filename: string): string | null {
  const base = filename.replace(/\\/g, "/").split("/").pop() ?? filename;
  const iv = base.match(IV_PATTERN);
  if (iv?.[1] != null) {
    return iv[1];
  }
  const pl = base.match(PL_PATTERN);
  if (pl?.[1] != null) {
    return pl[1];
  }
  return null;
}

function isApInvoiceEmailDocument(kind: string, filename: string | null | undefined): boolean {
  if (kind === "email") return true;
  const lower = (filename ?? "").toLowerCase();
  return lower.endsWith(".html") || lower.endsWith(".htm") || lower.endsWith(".eml");
}

function dedupeKey(filename: string): string {
  return (filename ?? "").trim().toLowerCase();
}

export function suggestApInvoiceDocumentGroups(
  documents: ApInvoiceDocumentGroupInput[],
): ApInvoiceDocumentGroupSuggestion {
  const warnings: string[] = [];
  const seenFilename = new Set<string>();
  const duplicateDocumentIds: string[] = [];
  const activeDocuments: ApInvoiceDocumentGroupInput[] = [];

  for (const document of documents) {
    const key = dedupeKey(document.filename);
    if (seenFilename.has(key)) {
      duplicateDocumentIds.push(document.id);
      warnings.push(`Duplicate filename skipped for grouping: ${document.filename}`);
      continue;
    }
    seenFilename.add(key);
    activeDocuments.push(document);
  }

  const unassigned: string[] = [];
  const byKey = new Map<string, ApInvoiceDocumentGroupInput[]>();

  for (const document of activeDocuments) {
    if (isApInvoiceEmailDocument(document.kind, document.filename)) {
      unassigned.push(document.id);
      continue;
    }
    const invoiceKey = parseSuntronicInvoiceKeyFromFilename(document.filename);
    if (invoiceKey == null) {
      unassigned.push(document.id);
      continue;
    }
    const bucket = byKey.get(invoiceKey) ?? [];
    bucket.push(document);
    byKey.set(invoiceKey, bucket);
  }

  const stillUnassigned: string[] = [];
  for (const documentId of unassigned) {
    const document = activeDocuments.find((candidate) => candidate.id === documentId);
    if (document == null || isApInvoiceEmailDocument(document.kind, document.filename)) {
      stillUnassigned.push(documentId);
      continue;
    }
    const matchedKey = [...byKey.keys()].find((invoiceKey) =>
      new RegExp(`(?<!\\d)${invoiceKey}(?!\\d)`).test(document.filename),
    );
    if (matchedKey == null) {
      stillUnassigned.push(documentId);
      continue;
    }
    byKey.get(matchedKey)?.push(document);
  }

  const groups: ApInvoiceSuggestedDocumentGroup[] = [];
  for (const [invoiceKey, members] of [...byKey.entries()].sort((left, right) =>
    left[0].localeCompare(right[0], undefined, { numeric: true }),
  )) {
    groups.push({
      invoice_key: invoiceKey,
      suggested_mfr_invoice_number: invoiceKey,
      document_ids: members.map((member) => member.id),
      filenames: members.map((member) => member.filename),
    });
  }

  const invoicePdfGroups = groups.filter((group) =>
    group.filenames.some((name) => /\bIV\s*0*\d+/i.test(name)),
  );

  return {
    groups,
    unassigned_document_ids: stillUnassigned,
    duplicate_document_ids: duplicateDocumentIds,
    suggested_invoice_count: invoicePdfGroups.length > 0 ? invoicePdfGroups.length : groups.length,
    warnings,
  };
}

export function apInvoiceSplitSuggestionBadgeLabel(suggestedInvoiceCount: number, documentCount: number): string | null {
  if (documentCount <= 1) return null;
  if (suggestedInvoiceCount > 1) {
    return `${suggestedInvoiceCount} invoice PDFs detected`;
  }
  return `${documentCount} attachments`;
}
